/**
 * A horizontal carousel that loops in both directions and advances on its own.
 *
 * Every behavioural difference between the sections that use it is expressed as
 * a data attribute, so the element itself stays generic and each section keeps
 * its own selectors and class names in its Liquid:
 *
 *   data-carousel            "true" to run at all; anything else leaves the
 *                            markup as plain, non-scrolling content
 *   data-track               selector for the scrolling element
 *   data-item                selector for the slides inside it
 *   data-clone-class         class added to the duplicated slides
 *   data-autoplay-interval   seconds between automatic moves (default 4)
 *   data-mobile-only         "true" to run only below 750px, for sections whose
 *                            desktop layout is a grid rather than a carousel
 *   data-manage-video        "true" to keep only the centre slide and its
 *                            neighbours playing
 *
 * The loop works by appending two inert copies of the slide set and resting on
 * the middle copy, so there is always material on both sides: swiping backwards
 * from the first slide has somewhere to go instead of hitting scrollLeft 0. All
 * three sets are identical, so shifting by exactly one set's width is invisible.
 *
 * @example
 * <carousel-component
 *   class="icon-row"
 *   data-carousel="true"
 *   data-track=".icon-row__track"
 *   data-item=".icon-with-text"
 *   data-clone-class="icon-row__clone"
 *   data-mobile-only="true"
 * >
 */

const MOBILE_QUERY = '(max-width: 749px)';
const RESUME_DELAY = 4000;
const SETTLE_DELAY = 180;

export class CarouselComponent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.carousel !== 'true') return;

    this.track = this.querySelector(this.dataset.track);
    if (!this.track) return;

    this.itemSelector = this.dataset.item;
    this.mobileOnly = this.dataset.mobileOnly === 'true';
    this.managesVideo = this.dataset.manageVideo === 'true';
    this.interval = Number(this.dataset.autoplayInterval || 4) * 1000;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = window.matchMedia(MOBILE_QUERY);

    this.handleViewportChange = () => this.sync();
    this.handleScroll = () => this.onScroll();
    this.handlePointerDown = () => this.hold();
    this.handlePointerUp = () => this.release();
    this.handleVisibility = () => this.sync();

    this.cloneItems();

    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.handlePrevious = () => this.step(-1);
    this.handleNext = () => this.step(1);
    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);

    this.mobile.addEventListener('change', this.handleViewportChange);
    this.reducedMotion.addEventListener('change', this.handleViewportChange);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
    this.track.addEventListener('pointerup', this.handlePointerUp, { passive: true });
    this.track.addEventListener('pointercancel', this.handlePointerUp, { passive: true });

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      this.sync();
    });
    this.observer.observe(this);

    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.mobile?.removeEventListener('change', this.handleViewportChange);
    this.reducedMotion?.removeEventListener('change', this.handleViewportChange);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.handlePointerUp);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
  }

  /** The real slides plus the clones, in document order. */
  get items() {
    return this.track.querySelectorAll(`:scope > ${this.itemSelector}`);
  }

  /**
   * Appends two inert copies of every slide.
   *
   * Only elements matching the item selector are copied, never `> *`: a block
   * that declares `"tag": null` renders its scoped stylesheet as a sibling in
   * this container, and cloning that would duplicate CSS and put a <style>
   * element into the slide flow.
   */
  cloneItems() {
    if (this.cloned) return;

    const items = Array.from(this.items);
    if (items.length === 0) return;

    this.firstItem = items[0];
    const cloneClass = this.dataset.cloneClass;

    for (let pass = 0; pass < 2; pass++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);

        if (cloneClass) clone.classList.add(cloneClass);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('a, button').forEach((node) => node.setAttribute('tabindex', '-1'));

        // Three copies of the same clip would be three audio tracks.
        clone.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          video.setAttribute('muted', '');
        });

        this.firstClone ||= clone;
        this.track.appendChild(clone);
      });
    }

    this.cloned = true;
  }

  /** Autoplay runs only while visible, in scope, and with motion allowed. */
  sync() {
    const inScope = !this.mobileOnly || this.mobile.matches;

    if (inScope) {
      this.positionAtRest();
    } else {
      this.track.scrollLeft = 0;
      this.positioned = false;
    }

    if (this.visible && inScope && !this.reducedMotion.matches && !document.hidden) {
      this.start();
    } else {
      this.stop();
    }
  }

  /** Park on the middle set once the carousel layout has actually applied. */
  positionAtRest() {
    if (this.positioned) return;

    const loop = this.loopWidth();
    if (loop <= 0) return;

    this.track.scrollLeft = loop;
    this.positioned = true;
    this.updateActive();
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.advance(), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    clearTimeout(this.resumeTimer);
    clearTimeout(this.idleTimer);
    this.timer = null;
  }

  hold() {
    this.holding = true;
    clearInterval(this.timer);
    clearTimeout(this.resumeTimer);
    this.timer = null;
  }

  release() {
    this.holding = false;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => this.sync(), RESUME_DELAY);
  }

  advance() {
    if (this.holding) return;
    this.step(1);
  }

  /**
   * Moves one slide in `direction`. A manual tap also restarts the autoplay
   * clock, so the next automatic move is a full interval away instead of
   * landing immediately after the tap.
   *
   * Wraps first, then scrolls: normalising mid-animation would cancel the
   * smooth scroll and show the jump.
   */
  step(direction) {
    this.normalize();

    const step = this.stepWidth();
    if (step <= 0) return;

    this.track.scrollTo({ left: this.track.scrollLeft + step * direction, behavior: 'smooth' });

    if (this.timer) {
      this.stop();
      this.start();
    }
  }

  /**
   * `offsetWidth`, never `getBoundingClientRect()`: a carousel that scales its
   * inactive slides would report the transformed size, making every step a
   * fraction short until the track drifted out of alignment.
   */
  stepWidth() {
    if (!this.firstItem) return 0;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.firstItem.offsetWidth + gap;
  }

  /**
   * One set wide, measured as the first clone's offset rather than a third of
   * the scroll width: the track has one gap fewer than it has slides, so
   * dividing would leave the wrap off by a fraction of a gap and the jump would
   * show.
   */
  loopWidth() {
    if (!this.firstItem || !this.firstClone) return 0;

    return this.firstClone.offsetLeft - this.firstItem.offsetLeft;
  }

  /** Keep the resting band at [loop, 2 * loop) by shifting a whole set. */
  normalize() {
    const loop = this.loopWidth();
    if (loop <= 0) return;

    if (this.track.scrollLeft >= loop * 2) {
      this.track.scrollLeft -= loop;
    } else if (this.track.scrollLeft < loop) {
      this.track.scrollLeft += loop;
    }
  }

  /**
   * Marks the slide nearest the track's centre so the CSS can bring it forward
   * and let the neighbours recede. Measured against the scrollport's centre
   * rather than the viewport's, because the track is the element that scrolls.
   */
  updateActive() {
    const items = Array.from(this.items);
    if (items.length === 0) return;

    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = null;
    let smallest = Infinity;

    items.forEach((item) => {
      const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - middle);
      if (distance < smallest) {
        smallest = distance;
        closest = item;
      }
    });

    const activeIndex = items.indexOf(closest);

    items.forEach((item, index) => {
      item.classList.toggle('is-active', item === closest);

      if (!this.managesVideo) return;

      /**
       * Cloning triples the clips, so playing them all would be three times the
       * streams the section actually shows. Only the centre slide and its
       * immediate neighbours run; the rest hold on their preview image.
       */
      const near = Math.abs(index - activeIndex) <= 1;

      item.querySelectorAll('video').forEach((video) => {
        if (near) {
          // Rejected by the autoplay policy until the visitor interacts; the
          // poster stays up in that case, which is the right fallback.
          video.play?.().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    });
  }

  /** Wrap a manual swipe once it settles, never while it is still moving. */
  onScroll() {
    this.updateActive();
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (!this.holding) this.normalize();
    }, SETTLE_DELAY);
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
